import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { Channel, ServerMessageEnvelope, zServerMessageEnvelope } from "@/gen/realtime";
import { wsUrl } from "@/utils/backend";
import { reportError } from "@/utils/errors";
import { Opaque } from "@/utils/opaque";

import { ListenFn, RealTimeContext } from "./context";

type Listener = {
    fn: ListenFn;
    channel: Channel;
    // easier to compare
    channelString: ChannelAsString;
};

export const RealtimeProvider = ({ children }: { children: ReactNode }) => {
    const [error, setError] = useState<Error | null>(null);
    const listeners = useRef<Map<string, Listener>>(new Map());

    const socketRef = useRef<Socket | null>(null);

    const updateSubscriptions = useCallback(() => {
        const channels = new Set<Channel>();
        for (const listener of listeners.current.values()) {
            channels.add(listener.channel);
        }

        if (socketRef.current) {
            socketRef.current.send({
                t: "SetSubscriptions",
                c: { channels: Array.from(channels) },
            });
        }
    }, []);

    const addListener = useMemo(
        () => (channel: Channel, listener: ListenFn) => {
            const id = "id" + Math.random().toString(16).slice(2);
            listeners.current.set(id, {
                fn: listener,
                channel,
                channelString: channelAsString(channel),
            });

            updateSubscriptions();

            return id;
        },
        [updateSubscriptions]
    );

    const removeListener = useCallback(
        (id: string) => {
            listeners.current.delete(id);
            updateSubscriptions();
        },
        [updateSubscriptions]
    );

    useEffect(() => {
        const socket = startWebsocket({
            onMessage: (msg: ServerMessageEnvelope) => {
                // New listeners might be added when we loop, and we don't want to notify them.
                // That can (and has) created an infinite loop. So create a copy and iterate
                // over it.
                const channel = channelAsString(msg.channel);
                const listenersCopy = Array.from(listeners.current);
                for (const [_, listener] of listenersCopy) {
                    if (channel === listener.channelString) {
                        listener.fn(msg.message);
                    }
                }
            },
            onReady: () => {
                setError(null);
                updateSubscriptions();
                console.log("Websocket ready");
            },
            onError: err => {
                setError(err);
            },
        });

        socketRef.current = socket;

        return () => {
            socketRef.current = null;
            socket.close();
        };
    }, [updateSubscriptions]);

    return (
        <RealTimeContext.Provider value={{ addListener, removeListener, error }}>
            {children}
        </RealTimeContext.Provider>
    );
};

type Socket = ReturnType<typeof startWebsocket>;

function startWebsocket({
    onMessage,
    onReady,
    onError,
}: {
    onMessage: (msg: ServerMessageEnvelope) => void;
    onReady: () => void;
    onError: (err: Error) => void;
}) {
    let socket: undefined | WebSocket;
    let shouldClose = false;

    const onClose = (e: CloseEvent) => {
        if (!shouldClose) {
            console.log(
                `socket closed unexpectedly (code=${e.code}, reason=${e.reason}). Will reconnect...`
            );
            setTimeout(connect, 10000);
        }
    };

    const onOpen = () => {
        console.log("Websocket onOpen");
        onReady();
    };

    const innerOnMessage = (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data);
            onMessage(zServerMessageEnvelope.parse(data));
        } catch (e) {
            if (e instanceof z.ZodError) {
                console.log(
                    "Zod error parsing message. Check if the server message type changed",
                    e
                );
            }
        }
    };

    const innerOnError = (e: unknown) => {
        // Cast e to error if it is one

        reportError(e, "websocket error");
        onError(new Error("Error connecting to websocket"));
        reportError(e, "Websocket error");
    };

    const closeAndCleanupSocket = () => {
        if (socket) {
            socket.close();
            socket.removeEventListener("message", innerOnMessage);
            socket.removeEventListener("close", onClose);
            socket.removeEventListener("open", onOpen);
            socket.removeEventListener("error", innerOnError);
            socket = undefined;
        }
    };

    const close = () => {
        console.log("Closing websocket...");
        shouldClose = true;

        if (socket?.readyState === WebSocket.OPEN) {
            closeAndCleanupSocket();
        }
    };

    const connect = () => {
        closeAndCleanupSocket();

        console.log("Connecting to websocket...");
        // TODO: Switch url
        socket = new WebSocket(wsUrl());

        socket.addEventListener("message", innerOnMessage);
        socket.addEventListener("close", onClose);
        socket.addEventListener("open", onOpen);
        socket.addEventListener("error", innerOnError);
    };

    const send = (message: ClientMessage) => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            const serialized = JSON.stringify(message);
            socket.send(serialized);
        }
    };

    connect();

    return { close, send };
}

export type ClientMessage = {
    t: "SetSubscriptions";
    // List of channels to subscribe to
    c: {
        channels: Channel[];
    };
};

export const RealtimeStatus = () => {
    const realtimeError = useContext(RealTimeContext)?.error;

    if (!realtimeError) {
        return null;
    }

    return (
        <div className="z-100 absolute bottom-0 left-0 right-0 top-0 flex flex-col items-center justify-center gap-20 bg-gray-800 p-20 text-white">
            <span className="text-center text-4xl">
                Apologies, but we have some problems reaching our server. Please wait..
            </span>
            <span>{realtimeError.message}</span>
        </div>
    );
};

function channelAsString(channel: Channel): ChannelAsString {
    switch (channel.t) {
        case "GameUpdated":
            return `GameUpdated:${channel.c}` as ChannelAsString;

        case "CurrentGameChanged":
            return "CurrentGameChanged" as ChannelAsString;

        case "PlyEvaluationUpdated":
            return `PlyEvaluationUpdated:${channel.c}` as ChannelAsString;

        case "MultiPvPlyEvaluationUpdated":
            return `MultiPvPlyEvaluationUpdated:${channel.c}` as ChannelAsString;

        case "LiveEvaluation":
            return `LiveEvaluation:${channel.c}` as ChannelAsString;

        case "TournamentStandingsUpdated":
            return `TournamentStandingsUpdated:${channel.c}` as ChannelAsString;

        case "TournamentBracketsUpdated":
            return `TournamentBracketsUpdated:${channel.c}` as ChannelAsString;

        case "CountryStandingsUpdated":
            return `CountryStandingsUpdated:${channel.c}` as ChannelAsString;

        case "GamePliesUpdated":
            return `GamePliesUpdated:${channel.c}` as ChannelAsString;

        case "GameMetadataUpdated":
            return `GameMetadataUpdated:${channel.c}` as ChannelAsString;

        case "GameReportUpdated":
            return `GameReportUpdated:${channel.c}` as ChannelAsString;

        case "GuessedMovesUpdated":
            return `GuessedMovesUpdated:${channel.c.gameId}:${channel.c.userId}` as ChannelAsString;

        case "MachineAnalysesUpdated":
            return `MachineAnalysesUpdated:${channel.c}` as ChannelAsString;

        case "FollowedPlayersDataUpdated":
            return `FollowedPlayersDataUpdated:${JSON.stringify(channel.c)}` as ChannelAsString;

        case "GamesMetadataUpdated":
            return `GamesMetadataUpdated:${JSON.stringify(channel.c)}` as ChannelAsString;

        case "TrialUpdated":
            return `TrialUpdated:${channel.c}` as ChannelAsString;

        case "Trials":
            return `Trials:${JSON.stringify(channel.c)}` as ChannelAsString;

        case "unknown":
            return "Unknown" as ChannelAsString;

        default:
            return `MissingChannelAsString:${channel.t}` as ChannelAsString;
    }

    // Not reachable in practice
    throw new Error(`Unknown channel type: ${channel.t}`);
}

type ChannelAsString = Opaque<string, "ChannelAsString">;
